import { create } from 'zustand';

// Safe localStorage wrapper (iOS Safari can throw)
const safeStorage = {
  get: (key, fallback = null) => {
    try { return localStorage.getItem(key) ?? fallback; }
    catch {
      try { return sessionStorage.getItem(key) ?? fallback; }
      catch { return fallback; }
    }
  },
  set: (key, val) => {
    try { localStorage.setItem(key, val); }
    catch {
      try { sessionStorage.setItem(key, val); }
      catch {}
    }
  },
};

// ── Rate cache ────────────────────────────────────────────────────────────────
let _ratesCache = null;
let _fetchPromise = null;

async function fetchRates() {
  if (_ratesCache) return _ratesCache;
  if (_fetchPromise) return _fetchPromise;
  _fetchPromise = fetch('https://api.exchangerate-api.com/v4/latest/USD')
    .then(r => r.json())
    .then(data => {
      _ratesCache = data?.rates || {};
      return _ratesCache;
    })
    .catch(() => {
      _ratesCache = { GTQ: 7.75, EUR: 0.92, GBP: 0.79, MXN: 17.1, CAD: 1.36, AUD: 1.53, JPY: 149.5 };
      return _ratesCache;
    });
  return _fetchPromise;
}

// ── Popular display currencies (GTQ + USD first) ──────────────────────────────
export const DISPLAY_CURRENCIES = [
  { code: 'GTQ', label: 'GTQ — Guatemalan Quetzal' },
  { code: 'USD', label: 'USD — US Dollar' },
  { code: 'EUR', label: 'EUR — Euro' },
  { code: 'GBP', label: 'GBP — British Pound' },
  { code: 'MXN', label: 'MXN — Mexican Peso' },
  { code: 'CAD', label: 'CAD — Canadian Dollar' },
  { code: 'AUD', label: 'AUD — Australian Dollar' },
  { code: 'JPY', label: 'JPY — Japanese Yen' },
  { code: 'BRL', label: 'BRL — Brazilian Real' },
  { code: 'COP', label: 'COP — Colombian Peso' },
  { code: 'CHF', label: 'CHF — Swiss Franc' },
];

// ── Zustand store ─────────────────────────────────────────────────────────────
export const useCurrencyStore = create((set, get) => ({
  currency: safeStorage.get('display_currency', 'USD'),
  rates: null,
  loading: false,

  loadRates: async () => {
    if (get().rates) return;
    set({ loading: true });
    const rates = await fetchRates();
    set({ rates, loading: false });
  },

  setCurrency: (code) => {
    safeStorage.set('display_currency', code);
    set({ currency: code });
  },

  convert: (usdAmount) => {
    const { currency, rates } = get();
    if (!usdAmount && usdAmount !== 0) return 0;
    if (currency === 'USD' || !rates) return usdAmount;
    const rate = rates[currency];
    if (!rate) return usdAmount;
    return usdAmount * rate;
  },

  fmt: (usdAmount, opts = {}) => {
    const { currency, rates } = get();
    const { decimals, abs: takeAbs } = { decimals: 0, abs: false, ...opts };
    let amount = usdAmount ?? 0;
    if (takeAbs) amount = Math.abs(amount);
    if (currency !== 'USD' && rates?.[currency]) {
      amount = amount * rates[currency];
    }
    const maxDec = ['JPY', 'KRW', 'CLP'].includes(currency) ? 0 : decimals;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      maximumFractionDigits: maxDec,
      minimumFractionDigits: maxDec,
    }).format(amount);
  },

  fmtFull: (usdAmount, opts = {}) => {
    return useCurrencyStore.getState().fmt(usdAmount, { decimals: 2, ...opts });
  },
}));

export function useCurrency() {
  const store = useCurrencyStore();
  return {
    currency: store.currency,
    rates: store.rates,
    setCurrency: store.setCurrency,
    convert: store.convert,
    fmt: (n, opts) => store.fmt(n, opts),
    fmtFull: (n, opts) => store.fmtFull(n, opts),
  };
}
